/* eslint-disable camelcase */
import { paymentFields, ControlType } from './form-lib';
import { getEmptyTableFields } from './utils';
import { cloneDeep } from 'lodash';
import Mustache from 'mustache';

const checkInvalidCalc = (calculation, ctrlIndex) => {
  let calcResult = calculation.result;
  const tableField = calcResult.indexOf('_') > -1;
  calcResult = tableField ? calcResult.split('_')[0] : calcResult;

  const invalidCalc = !ctrlIndex[calcResult];
  const errorMsg = `Field ${calculation.result} no longer exist on form.`;

  if (invalidCalc) console.error('Calculation Error:', errorMsg);
  return invalidCalc;
};

const getRatingValue = (column, table_props, columnCtrlValue) => {
  const weights = table_props[column].weights;
  const option_values = table_props[column].option_values;
  // Resolve legacy weights
  const weight = option_values ? weights[columnCtrlValue - 1] || weights[0] || 1 : 1;
  const result = columnCtrlValue * weight;
  return result;
};

const columnHandler = {};

columnHandler.radioButtonHandler = (ctrl, column, columnCtrlValue) => {
  const idx = ctrl.table_props[column].options.indexOf(columnCtrlValue);
  if (idx === -1) return 0;
  return ctrl.table_props[column].option_values ? ctrl.table_props[column].weights[idx] : 1;
};

columnHandler.selectBasicHandler = (ctrl, column, columnCtrlValue) => {
  return columnHandler.radioButtonHandler(ctrl, column, columnCtrlValue);
};

columnHandler.checkboxesHandler = (ctrl, column, columnCtrlValue) => {
  try {
    let total = 0;
    columnCtrlValue = columnCtrlValue || []; // Checkboxes value is expected as an array in tables

    columnCtrlValue.forEach(function (item) {
      const idx = ctrl.table_props[column].options.indexOf(item);
      if (idx !== -1) {
        const itemVal = ctrl.table_props[column].option_values ? ctrl.table_props[column].weights[idx] : 1;
        total += Number(itemVal);
      }
    });

    if (isNaN(total)) {
      throw new Error(`Invalid total: ${total}`);
    }

    return total;
  } catch (error) {
    console.error('Error in checkboxesHandler:', error);
    return 0;
  }
};

columnHandler.ratingHandler = (ctrl, column, columnCtrlValue) => {
  const {
    table_props,
    props: { column_types },
  } = ctrl;

  const columnType = column_types[column];

  const fallbackValue =
    {
      [ControlType.STAR_RATING]: '0 Star',
      [ControlType.HEART_RATING]: '0 Heart',
      [ControlType.SCALE_RATING]: 'Option 0',
    }[columnType] || '';

  const newColumnCtrlValue = columnCtrlValue || fallbackValue;
  const numericColumnCtrlValue = parseInt(newColumnCtrlValue.replace(/\D/g, ''), 10);

  const result = getRatingValue(column, table_props, numericColumnCtrlValue);
  return result;
};

const tableHandler = {
  star_rating: columnHandler.ratingHandler,
  heart_rating: columnHandler.ratingHandler,
  scale_rating: columnHandler.ratingHandler,
  checkboxes: columnHandler.checkboxesHandler,
  select_basic: columnHandler.selectBasicHandler,
  inline_radios: columnHandler.radioButtonHandler,
  radio_buttons: columnHandler.radioButtonHandler,
  inline_checkboxes: columnHandler.checkboxesHandler,
};

const extractValue = (ctrl) => {
  // fixes issue when a number starts with 0
  const pattern = /^0\d/;
  if (pattern.test(ctrl.value)) {
    ctrl.value = ctrl.value.toString().substr(1);
  }
  return ctrl.value;
};

const numberInputHandler = (ctrl) => extractValue(ctrl) || 0;

const textInputHandler = (ctrl) => {
  const validity = ctrl.props.type === 2;
  if (validity) return extractValue(ctrl) || 0;
  return ctrl.value || 0;
};

const totalChoices = (ctrl, ctrlValue) => {
  let total = 0;
  const arr = Object.keys(ctrlValue);

  for (const i in arr) {
    const item = arr[i];
    if (!ctrlValue[item]) continue;
    const { props } = ctrl;
    const idx = props.options.indexOf(item);
    const itemVal = props.option_values ? props.weights[idx] : 1;
    total += Number(itemVal);
  }

  return total;
};

const checkboxesHandler = (ctrl) => {
  const ctrlValue = ctrl.value || {};
  return totalChoices(ctrl, ctrlValue);
};

const radioButtonHandler = (ctrl) => {
  const ctrlValue = ctrl.value;
  const idx = ctrl.props.options.indexOf(ctrlValue);
  if (idx === -1) return 0;

  const value = ctrl.props.option_values ? ctrl.props.weights[idx] : 1;
  return value;
};

const imageChoiceHandler = (ctrl) => {
  const { rating_type } = ctrl.props;
  const radio = rating_type === 'radio';
  if (radio) return radioButtonHandler(ctrl);

  const newValue = {};
  if (ctrl.value?.length) {
    ctrl.value.forEach((item) => {
      newValue[item] = true;
    });
  }

  const newCtrl = {
    ...cloneDeep(ctrl),
    value: newValue,
  };

  return checkboxesHandler(newCtrl);
};

const selectBasicHandler = (ctrl) => {
  const ctrlValue = ctrl.value;
  const { props } = ctrl;
  const idx = props.options.indexOf(ctrlValue);
  if (idx === -1) return 0;
  return props.option_values ? props.weights[idx] : 1;
};

const ratingHandler = (ctrl) => {
  const {
    type,
    value,
    props: { option_values, weights = [] },
  } = ctrl;

  const fallbackValue =
    {
      [ControlType.STAR_RATING]: '0 Star',
      [ControlType.HEART_RATING]: '0 Heart',
      [ControlType.SCALE_RATING]: 'Option 0',
    }[type] || '';

  const ctrlValue = value || fallbackValue;
  const numericValue = parseInt(ctrlValue.replace(/\D/g, ''), 10);

  // Resolve legacy weights
  const weight = option_values ? weights[numericValue - 1] || weights[0] || 1 : 1;
  const result = numericValue * weight;
  return result;
};

const matrixHandler = (ctrl) => {
  let result = 0;
  const clickedHeaders = [];
  const {
    value,
    props: { row_headers, column_headers, weights, option_values },
  } = ctrl;

  if (value) {
    for (let i = 0; i < row_headers.length; i += 1) {
      if (typeof value[i] === 'string') {
        clickedHeaders.push(value[i].split(': ')?.pop()?.trim());
      }
    }

    if (clickedHeaders.length) {
      for (let r = 0; r < clickedHeaders.length; r += 1) {
        if (clickedHeaders[r] !== '---') {
          const weight = weights ? weights[column_headers.indexOf(clickedHeaders[r])] ?? 1 : 1;
          result += option_values ? weight : 1;
        }
      }
    }
  }

  return result;
};

const convertFromDayStarter = (date, separator, starter) => {
  const splitDate = date.split(' ');
  if (splitDate.length == 1) splitDate[1] = '';
  let newDate = splitDate[0].split(separator);
  if (starter === 'month') {
    newDate = `${newDate[1] + separator + newDate[0] + separator + newDate[2]} ${splitDate[1]}`;
  }
  if (starter === 'year') {
    newDate = `${newDate[2] + separator + newDate[1] + separator + newDate[0]} ${splitDate[1]}`;
  }
  return newDate;
};

const rangeTime = (ctrl, ctrlValue) => {
  const { separator, date_format: format } = ctrl.props;
  const splitValue = ctrlValue ? ctrlValue.toString().split(' to ') : [0, 0];
  // Since the DD-MM-YYYY or DD.MM.YYY or DD/MM/YYYY will return invalid when used with new Date()
  // It is important to convert to an acceptable format e.g month starter - 01/14/2023 or year starter - 2023.14.01
  if (format === 'DD/MM/YYYY') {
    return (
      new Date(convertFromDayStarter(splitValue[1], separator, 'month')).valueOf() -
      new Date(convertFromDayStarter(splitValue[0], separator, 'month')).valueOf()
    );
  }
  return new Date(splitValue[1]).valueOf() - new Date(splitValue[0]).valueOf();
};

const dayStarter = (ctrl, ctrlValue) => {
  const splitdate = ctrlValue.toString().split(' ');
  if (splitdate.length == 1) splitdate[1] = '';

  ctrlValue = splitdate[0].toString().split(ctrl.props.separator);
  const newDate = `${ctrlValue[2] + ctrl.props.separator + ctrlValue[1] + ctrl.props.separator + ctrlValue[0]} ${
    splitdate[1]
  }`;
  return new Date(newDate).valueOf();
};

const monthStarter = (ctrl, ctrlValue) => {
  const splitdate = ctrlValue.toString().split(' ');
  if (splitdate.length == 1) splitdate[1] = '';

  ctrlValue = splitdate[0].toString().split(ctrl.props.separator);
  const newDate = `${ctrlValue[2] + ctrl.props.separator + ctrlValue[0] + ctrl.props.separator + ctrlValue[1]} ${
    splitdate[1]
  }`;
  return new Date(newDate).valueOf();
};

const singleTime = (ctrl, ctrlValue) => {
  const DEFAULT = new Date(ctrlValue).valueOf();
  if (!ctrlValue) return DEFAULT;
  if (ctrl.props.mode === 'range') return rangeTime(ctrl, ctrlValue);
  if (ctrl.props.date_format === 'DD/MM/YYYY') return dayStarter(ctrl, ctrlValue);
  if (ctrl.props.date_format === 'MM/DD/YYYY') return monthStarter(ctrl, ctrlValue);
  return DEFAULT;
};

const genericFieldTime = (ctrl) => {
  if (ctrl.value) return new Date(`1970/01/01 ${ctrl.value}`).valueOf() + 3600000;
  return new Date('1970/01/01 01:00').valueOf();
};

const datetimeHandler = (ctrl) => {
  const ctrlValue = ctrl.value || 0;
  const propsType = ctrl.props.type;
  const propsMode = ctrl.props.mode;
  const single = (propsType === 0 || propsType === 1) && propsMode !== 'multiple';
  const generic = ctrl.props.type === 2;

  if (single) return singleTime(ctrl, ctrlValue);
  if (generic) return genericFieldTime(ctrl);
  return 0;
};

const TEXT_FIELD = ['short_text', 'text_input', 'labeled_input', 'prepended_text', 'appended_text'];

const TIME_MULTIPLIER = {
  years: 1000 * 60 * 60 * 24 * 365,
  months: 1000 * 60 * 60 * 24 * 30,
  days: 1000 * 60 * 60 * 24,
  hours: 1000 * 60 * 60,
  minutes: 1000 * 60,
  seconds: 1000,
};

const durationHandler = (ctrl) => {
  const ctrlValue = extractValue(ctrl) || 0;
  const propsUnit = ctrl.props.unit;
  const multiplier = TIME_MULTIPLIER[propsUnit];
  if (!multiplier) return ctrlValue;
  return ctrlValue * multiplier;
};

const OPERAND_HANDLERS = {
  datetime: datetimeHandler,
  duration: durationHandler,
  star_rating: ratingHandler,
  heart_rating: ratingHandler,
  scale_rating: ratingHandler,
  matrix_rating: matrixHandler,
  text_input: textInputHandler,
  checkboxes: checkboxesHandler,
  labeled_input: textInputHandler,
  appended_text: textInputHandler,
  prepended_text: textInputHandler,
  image_choice: imageChoiceHandler,
  select_basic: selectBasicHandler,
  number_input: numberInputHandler,
  radio_buttons: radioButtonHandler,
  inline_radios: radioButtonHandler,
  inline_checkboxes: checkboxesHandler,
};

const dayOrdinal = (day) => {
  const remainder = day % 10;
  const output =
    parseInt(day / 10) === 1 ? 'th' : remainder === 1 ? 'st' : remainder === 2 ? 'nd' : remainder === 3 ? 'rd' : 'th';
  return day + output;
};

const checkPayment = (calculation, ctrlIndex) => {
  const ctrlType = ctrlIndex[calculation.result]?.type;
  return paymentFields.indexOf(ctrlType) > -1;
};

const specialTime = {
  datetime: '{{month}} {{day}} {{year}}, {{hour}}:{{minutes}}:{{seconds}} {{meridian}}',
  date: '{{month}} {{day}} {{year}}',
  time: '{{hour}}:{{minutes}} {{meridian}}',
};

const formatDate = (dateValue, resultType) => {
  const dateObject = new Date(dateValue);
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const view = {
    year: dateObject.getUTCFullYear(),
    month: () => monthNames[dateObject.getMonth()],
    day: dayOrdinal(dateObject.getUTCDate()),
    hour: dateObject.getUTCHours(),
    minutes: dateObject.getUTCMinutes(),
    seconds: dateObject.getUTCSeconds(),
    meridian() {
      const meridian = this.hour > 11 ? 'pm' : 'am';
      return meridian;
    },
  };

  const dateFormat = specialTime[resultType] || specialTime.datetime;
  return Mustache.render(dateFormat, view);
};

export class Calculations {
  constructor({
    ctrl = {},
    ctrlIndex = {},
    userAmount = {},
    calculations = [],
    tableColumnResult = {},
    columnHasCalculation = {},
    tableColumnCalculations = {},
  } = {}) {
    this.ctrl = ctrl;
    this.resultFields = {};
    this.ctrlIndex = ctrlIndex;
    this.userAmount = userAmount;
    this.calculations = calculations;
    this.tableColumnResult = tableColumnResult;
    this.columnHasCalculation = columnHasCalculation;
    this.tableColumnCalculations = tableColumnCalculations;
  }

  apply() {
    const { ctrl, ctrlIndex, calculations } = this;
    
    if (ctrl.type === ControlType.TABLE_FIELD) {
      this.tableFieldChange();
    }

    for (const calc of calculations) {
      const invalidCalc = checkInvalidCalc(calc, ctrlIndex);
      if (invalidCalc) continue;

      const mixedCalc = calc.is_mixed;
      const simpleField = !calc.is_table;
      const datetimeCalc = calc.is_datetime || calc.datetime_result;
      const tableCalc =
        calc.is_table && ControlType.TABLE_FIELD && calc.operands[0].split('_')[0].substr(1) === ctrl.internal_id;

      if (mixedCalc) this.mixedCalculation(calc);
      else if (simpleField) this.simpleFieldCalc(calc);
      else if (datetimeCalc) this.datetimeFieldCalc(calc);
      else if (tableCalc) this.tableFieldCalculation(calc);
    }

    return { ...this };
  }

  getCalculationForColumn(ctrl, row, column) {
    const columnType = ctrl.props.column_types[column];
    const columnCtrlValue = ctrl.value[row][column];
    const handler = tableHandler[columnType];
    if (!handler) return Number(ctrl.value[row][column]) || 0;
    return handler(ctrl, column, columnCtrlValue);
  }

  sumChange(c) {
    const { ctrl, columnHasCalculation, tableColumnCalculations, tableColumnResult } = this;
    const ctrlVal = ctrl.value || [];
    columnHasCalculation[ctrl.internal_id] = true;
    tableColumnCalculations[ctrl.internal_id][c] = 'SUM - ';
    // calculate sum for column c
    let total = 0;
    for (let r = 0; r < ctrlVal.length; r++) {
      total += this.getCalculationForColumn(ctrl, r, c);
    }
    tableColumnResult[ctrl.internal_id][c] = total;
  }

  averageChange(c) {
    const { ctrl, columnHasCalculation, tableColumnCalculations, tableColumnResult } = this;
    const ctrlVal = ctrl.value || [];
    columnHasCalculation[ctrl.internal_id] = true;
    tableColumnCalculations[ctrl.internal_id][c] = 'AVERAGE - ';
    // calculate average for column c
    let total = 0;
    for (let r = 0; r < ctrlVal.length; r++) {
      total += this.getCalculationForColumn(ctrl, r, c);
    }
    const result = total / ctrlVal.length;
    tableColumnResult[ctrl.internal_id][c] = parseFloat(result.toString()).toFixed(2);
  }

  tableFieldChange() {
    const { ctrl } = this;
    for (let c = 0; c < ctrl.props.columns; c++) {
      const calcType = ctrl.table_props[c].column_calculation;
      if (calcType === 1) this.sumChange(c);
      else if (calcType === 2) this.averageChange(c);
    }
  }

  getOperandValuesForMixed(operands) {
    const that = this;
    let result = {};
    operands.forEach(function (operand) {
      const tableField = /^F/.test(operand);
      if (tableField) result = that.getTableFieldResult(operand, result);
      else result[`F${operand}`] = that.getValueForOperand(operand);
    });
    return result;
  }

  getTableFieldResult(operand, result) {
    const { ctrlIndex, tableColumnResult } = this;
    const ctrlId = operand.split('_')[0].substr(1);
    const columnId = operand.split('_')[1];
    const ctrl = ctrlIndex[ctrlId];
    const count = columnId === 'count';
    if (!ctrl) {
      // non existing fields
      // TO-DO: This fix won't work for calculation that has multiplication
      // or division.
      // Kindly fix this bug in the future.
      result[operand] = 0;
      return result;
    }
    if (count) {
      result[operand] = ctrl.value ? ctrl.value.length : 0;
      return result;
    }
    const columnCalculation = ctrl.table_props[columnId].column_calculation;
    const columnResult = tableColumnResult[ctrl.internal_id][columnId] || 0;
    result[operand] = columnCalculation ? columnResult : 0;
    return result;
  }

  getValueForOperand(operand) {
    const { ctrlIndex } = this;
    const ctrl = ctrlIndex[operand];
    if (!ctrl) return 0;
    const ctrlType = ctrl.type;

    const typeHandler = OPERAND_HANDLERS[ctrlType];
    if (typeHandler) return typeHandler(ctrl);
    return ctrl.value || 0;
  }

  getOperandValuesForTable(operands, row) {
    const { ctrlIndex } = this;
    const result = {};
    operands.forEach(function (operand) {
      const ctrlId = operand.split('_')[0].substr(1);
      const ctrl = ctrlIndex[ctrlId];
      const columnIdx = Number(operand.split('_')[1]);
      const ctrlType = ctrl.props.column_types[columnIdx];
      const ctrlValue = ctrl.value && ctrl.value[row] ? ctrl.value[row][columnIdx] : 0;
      const handler = tableHandler[ctrlType];
      result[operand] = handler ? handler(ctrl, columnIdx, ctrlValue) : ctrlValue || 0;
    });
    return result;
  }

  getOperandValues(operands) {
    const result = {};
    const that = this;
    operands.forEach(function (operand) {
      result[`F${operand}`] = that.getValueForOperand(operand);
    });
    return result;
  }

  getOperandValuesForDatetime(operands) {
    // same as getOperandValues
    return this.getOperandValues(operands);
  }

  mixedCalculation(calculation) {
    const { ctrlIndex, userAmount, resultFields } = this;
    let { operation } = calculation;
    const decimalPlaces = calculation.decimal_places;
    const operands = this.getOperandValuesForMixed(calculation.operands);
    operation = Mustache.render(operation, operands);

    const payment = checkPayment(calculation, ctrlIndex);
    const result = parseFloat(this.evaluate(operation).toString()).toFixed(decimalPlaces);
    const isResultInTable = calculation.result.includes('_');

    if (payment) userAmount[calculation.result] = result;
    else {
      if (isResultInTable) {
        const [tableId, tableColIndex] = calculation.result.split('_');
        const tableField = ctrlIndex[tableId];

        if (!tableField.value)
          tableField.value = getEmptyTableFields(tableField.props.min_rows, tableField.props.columns);
        const valLength = tableField.value?.length || 0;

        for (let row = 0; row < valLength; row++) {
          if (typeof tableField.value === 'string') continue;
          tableField.value[row][tableColIndex] = result;
          resultFields[tableId] = tableField;
        }
      } else {
        const resultField = ctrlIndex[calculation.result];
        resultFields[calculation.result] = resultField;
        resultFields[calculation.result].value = result;
      }
    }
  }

  tableFieldCalculation(calculation) {
    const ctrl = cloneDeep(this.ctrl);
    const valLength = ctrl.value ? ctrl.value.length : 0;
    const decimalPlaces = calculation.decimal_places;
    for (let row = 0; row < valLength; row++) {
      let { operation } = calculation;
      const operands = this.getOperandValuesForTable(calculation.operands, row);
      operation = Mustache.render(operation, operands);

      if (typeof ctrl.value === 'string') continue;
      const result = parseFloat(this.evaluate(operation).toString()).toFixed(decimalPlaces);
      ctrl.value[row][calculation.result.split('_')[1]] = result;
    }
    this.ctrl = ctrl;
    this.resultFields[calculation.result.split('_')[0]] = ctrl;
  }

  datetimeFieldCalc(calculation) {
    const { ctrlIndex, resultFields } = this;
    let { operation } = calculation;
    const operands = this.getOperandValuesForDatetime(calculation.operands);
    operation = Mustache.render(operation, operands);
    const dtValue = this.evaluate(operation);
    const resultType = calculation.datetime_result;
    const regularTime = TIME_MULTIPLIER[resultType];
    const textControl = TEXT_FIELD.includes(this.resultFields[calculation.result].type);
    let result = regularTime ? `${parseInt(dtValue / regularTime)} ${resultType}` : formatDate(dtValue, resultType);
    result = textControl ? result : parseFloat(result);
    const resultField = ctrlIndex[calculation.result];
    resultFields[calculation.result] = resultField;
    resultFields[calculation.result].value = result.toString();
  }

  simpleFieldCalc(calculation) {
    const { ctrlIndex, userAmount, resultFields } = this;
    let { operation } = calculation;
    const operands = this.getOperandValues(calculation.operands);
    const decimalPlaces = calculation.decimal_places;
    operation = Mustache.render(operation, operands);
    const payment = checkPayment(calculation, ctrlIndex);
    const result = parseFloat(this.evaluate(operation).toString()).toFixed(decimalPlaces);
    if (payment) {
      userAmount[calculation.result] = result;
    } else {
      const resultField = ctrlIndex[calculation.result];
      resultFields[calculation.result] = resultField;
      resultFields[calculation.result].value = result;
    }
  }

  evaluate(str) {
    let result = 0;
    let fixedStr = '';
    try {
      // Replace spaces between only digits and opening and/or closing parentheses with *
      fixedStr = str.replace(/(\d+)\s+\(/g, '$1*(').replace(/\)\s+(\d+)/g, ')*$1');
      result = Function(`return ${fixedStr}`)();
      // result = eval(fixedStr)
      // bug, other possible values eval or Function can return
    } catch (err) {
      const errMsg = `Calculation payload: ${fixedStr}\n` + `Calculation error ${err}`;
      console.error('Calculation Error', errMsg, err);
      window.errorReporter.report(err);
    }
    return result;
  }
}

export default {
  Calculations,
};
